import { useState } from 'react';
import { Check, CircleAlert, LoaderCircle } from 'lucide-react';
import type { Lead, MessageView } from '@upm/domain';
import { Avatar, Wordmark } from '../ui';
import { HelpDialog } from './HelpDialog';

/**
 * Navy header for firm-facing pages: logo | firm name, autosave status, "Questions? <lead>".
 * The lead pill opens the help dialog (needs a firm session; pass `lead` only on session pages).
 */
export function FirmHeader({ subtitle, staff, lead, saveLabel, saveState, onMessageSent }: {
  subtitle: string; staff?: boolean; lead?: Lead | null; saveLabel?: string; saveState?: 'ok' | 'saving' | 'error'; onMessageSent?: (m: MessageView) => void;
}) {
  const [asking, setAsking] = useState(false);
  return (
    <>
    <header className="bg-brand text-header-fg">
      <div className="mx-auto flex max-w-shell flex-wrap items-center gap-x-4 gap-y-2.5 px-4 py-3 sm:px-8 sm:py-4">
        <div className="flex min-h-9 min-w-0 items-center gap-3.5">
          <Wordmark staff={staff} />
          <span className="min-w-0 border-l border-header-rule pl-3.5 text-sm font-semibold text-header-fg [overflow-wrap:anywhere]">{subtitle}</span>
        </div>
        <div className="flex flex-wrap items-center gap-x-4 gap-y-2 text-[13px] sm:ml-auto">
          {saveLabel !== undefined && (
            <span className="inline-flex items-center gap-1.5 text-header-nav" role="status" aria-live="polite">
              {saveState === 'saving' ? <LoaderCircle aria-hidden="true" size={16} className="animate-spin" />
                : saveState === 'error' ? <CircleAlert aria-hidden="true" size={16} className="text-[#F5C46B]" />
                : <Check aria-hidden="true" size={16} strokeWidth={2} />}
              {saveLabel}
            </span>
          )}
          {lead && (
            <button
              type="button"
              aria-haspopup="dialog"
              onClick={() => setAsking(true)}
              className="inline-flex min-h-9 items-center gap-2 rounded-pill border border-header-rule bg-transparent py-1 pl-1.5 pr-3 text-[13px] font-semibold text-white hover:bg-nav-active hover:text-white"
            >
              <Avatar initials={lead.initials} size={24} tone="soft" />
              Questions? {lead.name}<span className="sr-only">, {lead.title}</span>
            </button>
          )}
        </div>
      </div>
    </header>
    {lead && <HelpDialog open={asking} lead={lead} onClose={() => setAsking(false)} onSent={onMessageSent} />}
    </>
  );
}

export function FirmPage({ children }: { children: React.ReactNode }) {
  return <div className="min-h-screen bg-page">{children}</div>;
}
